// store/profileStore.ts
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { showNotification } from "@mantine/notifications";
import type {
  ClientFieldError,
  DocumentItem,
  Header,
  OCRState,
  ProfileTabs,
  SelectedDoc,
} from "../types";
import { isProfileTab } from "../types";
import type { Job } from "@/types";
import { INVALID_DIAGNOSTIC_ERRORS } from "../constants/constants";
import {
  getClientProfile,
  deleteDoc,
  toggleExtraction,
  getExtractionOCRData,
} from "../api";
import type { AxiosError } from "axios";

const EMPTY_DOC: DocumentItem = {
  docId: "",
  presignedUrl: "",
  label: "",
  fileName: "",
  userType: "spouse",
  key: "",
  documentType: "",
  documentCategory: "other",
  reviewed: false,
  confidence: null,
  ocrExtractions: [],
};

type ProfileStore = {
  // State variables
  activeDocument: string | null;
  activeTab: ProfileTabs;
  activeYear: number | null;
  availableYears: number[];
  condensed: boolean;
  contentData?: Record<string, any>;
  documents: DocumentItem[]; // Flat list of documents
  diagnosticErrors: Record<string, ClientFieldError[]>;
  headerData?: Header;
  isSplitView: boolean;
  job: Job | null;
  loaderVisible: boolean;
  ocrDataCache: Record<string, { data: any; version: number }>;
  ocrJobStatus: string | null;
  ocrProcessingDocId: string | null;
  ocrState: OCRState;
  oneClickState: string | null;
  refresh: number;
  tabLoading: boolean;
  review: boolean;
  selectedDoc: SelectedDoc;
  selectedExtractionIds: Record<string, number>;
  pendingExtractionSelection: {
    docId: string | null;
    documentName: string | null;
  };
  sidebarCollapsed: boolean;

  // API state
  loading: boolean;
  error: AxiosError | null;
  userId: string | null;

  // Intake flow flags
  createdFromIntakeFlow: boolean;
  invitedToPortal: boolean;

  // Archived documents
  hasArchivedDocuments: boolean;
  showArchivedDocuments: boolean;
  setShowArchivedDocuments: (show: boolean) => Promise<void>;

  // State setters and methods
  clearOcrProcessingDocId: () => void;
  clearPendingExtractionSelection: () => void;
  fetchProfile: (
    userId: string,
    taxYear?: number,
    includeEngagementLetter?: boolean,
    includeArchived?: boolean
  ) => Promise<void>;
  getSelectedExtractionId: (docId: string) => number | null;
  getOCRDataForDocument: (docId: string, extractionId?: number) => any | null;
  fetchOCRDataForDocument: (
    docId: string,
    extractionId?: number
  ) => Promise<any | null>;
  invalidateOCRData: () => void;
  refetch: () => Promise<void>;
  removeOCRData: (docId: string) => void;
  reset: () => void;

  // Mutation methods
  updateProfileField: (
    userId: string,
    key: string,
    value: any
  ) => Promise<void>;
  addDynamicItem: (userId: string, key: string) => Promise<void>;
  deleteDynamicItem: (userId: string, key: string) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
  toggleExtractionEnabled: (
    extractionId: number,
    enabled: boolean
  ) => Promise<void>;
  setActiveDocument: (docId: string | null) => void;
  setActiveTab: (tab: string | null) => void;
  setActiveYear: (year: number | null) => void;
  setAvailableYears: (years: number[]) => void;
  setContentData: (data: Record<string, any> | undefined) => void;
  setDocuments: (documents: DocumentItem[]) => void;
  setDiagnosticErrors: (
    fieldErrors: Record<string, ClientFieldError[]>
  ) => void;
  setHeaderData: (data: Header | undefined) => void;
  setIsSplitView: (isSplitView: boolean) => void;
  setJob: (job: Job | null) => void;
  setLoaderVisible: (visible: boolean) => void;
  setOCRData: (docId: string, data: any) => void;
  setOCRJobStatus: (status: string | null) => void;
  setOCRState: (state: OCRState) => void;
  setOcrProcessingDocId: (docId: string | null) => void;
  setOneClickState: (state: string | null) => void;
  setPendingExtractionSelection: (
    docId: string | null,
    documentName: string | null
  ) => void;
  setRefresh: (value: number) => void;
  setReview: (value: boolean) => void;
  setSelectedDoc: (doc: SelectedDoc | null) => void;
  setSelectedExtractionId: (docId: string, extractionId: number) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTabLoading: (loading: boolean) => void;
  triggerEmailRefresh: () => void;
};

/**
 * large store so need to optimize with subscribeWithSelector
 * each UI component should only subscribe to the parts of the store that it needs
 * this way Zustand tracks the previous result of your selector.
 * If the selector result hasn't changed, it doesn't notify the component at all.

 */
export const useProfileStore = create<ProfileStore>()(
  subscribeWithSelector((set, get) => ({
    // State variables
    refresh: 0,
    review: false,
    condensed: true,
    loaderVisible: false,
    availableYears: [] as number[],
    activeYear: null,
    job: null,
    ocrState: "none" as OCRState,
    oneClickState: null,
    diagnosticErrors: {},
    headerData: undefined,
    contentData: undefined,
    documents: [],
    ocrDataCache: {},
    activeDocument: null,
    isSplitView: false,
    selectedDoc: EMPTY_DOC,
    sidebarCollapsed: false,
    ocrJobStatus: null,
    ocrProcessingDocId: null,
    tabLoading: false,
    activeTab: "taxinfo" as ProfileTabs,
    selectedExtractionIds: {},
    pendingExtractionSelection: {
      docId: null,
      documentName: null,
    },

    // API state
    loading: false,
    error: null,
    userId: null,

    // Intake flow flags
    createdFromIntakeFlow: false,
    invitedToPortal: false,

    // Archived documents
    hasArchivedDocuments: false,
    showArchivedDocuments: false,

    // State setters
    setRefresh: (refresh) => set({ refresh }),
    setReview: (review) => set({ review }),
    setLoaderVisible: (visible) => set({ loaderVisible: visible }),
    setAvailableYears: (years) => set({ availableYears: years }),
    setActiveYear: (year) => set({ activeYear: year }),
    setJob: (job) => set({ job }),
    setOneClickState: (state) => set({ oneClickState: state }),
    setDiagnosticErrors: (diagnosticErrors) => {
      // Filter out invalid diagnostic errors when setting them
      const filtered: Record<string, ClientFieldError[]> = {};

      Object.entries(diagnosticErrors).forEach(([key, errors]) => {
        filtered[key] = errors.filter((error) => {
          if (error.type === "ocr_error" && "suggested" in error) {
            return !INVALID_DIAGNOSTIC_ERRORS.includes(error.suggested);
          }
          return true;
        });
      });

      set({ diagnosticErrors: filtered });
    },
    setHeaderData: (headerData) => set({ headerData }),
    setContentData: (contentData) => set({ contentData }),
    setDocuments: (documents) => set({ documents }),
    setIsSplitView: (isSplitView) => set({ isSplitView }),
    setSidebarCollapsed: (collapsed: boolean) =>
      set({ sidebarCollapsed: collapsed }),
    setOCRState: (state: OCRState) => set({ ocrState: state }),
    setOCRData: (docId, data) =>
      set((state) => ({
        ocrDataCache: {
          ...state.ocrDataCache,
          [docId]: {
            data,
            version: (state.ocrDataCache[docId]?.version ?? 0) + 1,
          },
        },
      })),
    removeOCRData: (docId) =>
      set((state) => {
        const newCache = { ...state.ocrDataCache };
        delete newCache[docId];
        return { ocrDataCache: newCache };
      }),
    invalidateOCRData: () => set({ ocrDataCache: {} }),
    setActiveDocument: (docId) => set({ activeDocument: docId }),
    setSelectedDoc: (doc) => {
      if (doc === null) {
        set({ selectedDoc: EMPTY_DOC });
      } else {
        set({ selectedDoc: doc });
      }
    },
    triggerEmailRefresh: () => set((state) => ({ refresh: state.refresh + 1 })),
    setOCRJobStatus: (status) => set({ ocrJobStatus: status }),
    setOcrProcessingDocId: (docId: string | null) =>
      set({ ocrProcessingDocId: docId }),
    clearOcrProcessingDocId: () => set({ ocrProcessingDocId: "" }),
    setActiveTab: (tab) => {
      set({ activeTab: isProfileTab(tab) });
    },
    setTabLoading: (tabLoading: boolean) => set({ tabLoading }),
    setSelectedExtractionId: (docId: string, extractionId: number) =>
      set((state) => ({
        selectedExtractionIds: {
          ...state.selectedExtractionIds,
          [docId]: extractionId,
        },
      })),
    getSelectedExtractionId: (docId: string) => {
      return get().selectedExtractionIds[docId] ?? null;
    },
    getOCRDataForDocument: (docId: string, extractionId?: number) => {
      const { selectedExtractionIds, ocrDataCache } = get();

      // Build cache key
      const selectedId = extractionId ?? selectedExtractionIds[docId];
      const cacheKey = selectedId ? `${docId}_${selectedId}` : docId;

      // Return from cache (or null if not yet fetched)
      return ocrDataCache[cacheKey]?.data ?? null;
    },
    fetchOCRDataForDocument: async (docId: string, extractionId?: number) => {
      const { documents, selectedExtractionIds, ocrDataCache } = get();

      // Build cache key
      const selectedId = extractionId ?? selectedExtractionIds[docId];
      const cacheKey = selectedId ? `${docId}_${selectedId}` : docId;

      // Already cached
      if (ocrDataCache[cacheKey]) {
        return ocrDataCache[cacheKey].data;
      }

      // Find document to check if it has extractions
      const document = documents.find((doc) => doc.docId === docId);
      if (!document || !document.ocrExtractions?.length) {
        return null;
      }

      // Find the extraction to check hasOcrData
      const extraction = selectedId
        ? document.ocrExtractions.find((ext) => ext.id === selectedId)
        : document.ocrExtractions[0];

      if (!extraction?.meta?.hasOcrData) {
        return null;
      }

      try {
        // Lazy-load OCR data from the forms data endpoint
        const ocrData = await getExtractionOCRData(docId, selectedId);
        get().setOCRData(cacheKey, ocrData);
        return ocrData;
      } catch (error) {
        console.error(
          "[fetchOCRDataForDocument] Failed to fetch OCR data:",
          error
        );
        return null;
      }
    },
    setPendingExtractionSelection: (
      docId: string | null,
      documentName: string | null
    ) =>
      set({
        pendingExtractionSelection: { docId, documentName },
      }),
    clearPendingExtractionSelection: () =>
      set({
        pendingExtractionSelection: { docId: null, documentName: null },
      }),

    setShowArchivedDocuments: async (show: boolean) => {
      set({ showArchivedDocuments: show });
      const { userId, activeYear } = get();
      if (userId) {
        await get().fetchProfile(userId, activeYear ?? undefined, true, show);
      }
    },

    // API methods
    fetchProfile: async (
      userId: string,
      taxYear?: number,
      includeEngagementLetter: boolean = true,
      includeArchived: boolean = false
    ) => {
      console.log("[ProfileStore] fetchProfile called:", {
        userId,
        taxYear,
        stack: new Error().stack?.split("\n").slice(2, 5).join("\n"),
      });

      set({ loading: true, error: null, userId });

      try {
        const response = await getClientProfile(
          userId,
          taxYear,
          includeEngagementLetter,
          includeArchived
        );

        console.log("[ProfileStore] fetchProfile response received");

        const {
          header,
          body,
          documents,
          diagnosticErrors,
          oneClickState: newOneClickState,
          ocrState,
          availableYears,
          createdFromIntakeFlow,
          invitedToPortal,
          hasArchivedDocuments,
        } = response.content;

        // Filter out invalid diagnostic errors
        const filtered: Record<string, ClientFieldError[]> = {};
        Object.entries(diagnosticErrors || {}).forEach(([key, errors]) => {
          filtered[key] = errors.filter((error) => {
            if (error.type === "ocr_error" && "suggested" in error) {
              return !INVALID_DIAGNOSTIC_ERRORS.includes(error.suggested);
            }
            return true;
          });
        });

        // Update all state at once to minimize re-renders
        console.log("[ProfileStore] Updating state with fetched data");
        set({
          loading: false,
          error: null,
          headerData: header as unknown as Header,
          contentData: body,
          documents: documents || [],
          diagnosticErrors: filtered,
          oneClickState: newOneClickState || null,
          ocrState: ocrState || "none",
          availableYears: availableYears || [],
          createdFromIntakeFlow: createdFromIntakeFlow ?? false,
          invitedToPortal: invitedToPortal ?? false,
          hasArchivedDocuments: hasArchivedDocuments ?? false,
        });

        // Set active year if not set and we have available years
        const currentActiveYear = get().activeYear;
        if (!currentActiveYear && availableYears && availableYears.length > 0) {
          const yearToSet = taxYear || availableYears[0];
          console.log("[ProfileStore] Setting activeYear:", yearToSet);
          set({ activeYear: yearToSet });
        }
      } catch (error) {
        console.error("Error fetching client profile:", error);
        set({ loading: false, error: error as AxiosError });
      }
    },

    refetch: async () => {
      const { userId, activeYear, showArchivedDocuments } = get();
      console.log("[ProfileStore] refetch called:", { userId, activeYear });
      if (userId) {
        await get().fetchProfile(
          userId,
          activeYear ?? undefined,
          true,
          showArchivedDocuments
        );
      }
    },

    reset: () =>
      set({
        selectedDoc: EMPTY_DOC,
        isSplitView: false,
        headerData: undefined,
        contentData: undefined,
        documents: [],
        availableYears: [],
        activeYear: null,
        job: null,
        oneClickState: null,
        review: false,
        loaderVisible: false,
        selectedExtractionIds: {},
        pendingExtractionSelection: { docId: null, documentName: null },
        loading: false,
        error: null,
        userId: null,
        createdFromIntakeFlow: false,
        invitedToPortal: false,
        hasArchivedDocuments: false,
        showArchivedDocuments: false,
      }),

    // Mutation methods
    updateProfileField: async (userId: string, key: string, value: any) => {
      const activeYear = get().activeYear;

      if (!activeYear) {
        console.error(
          "[ProfileStore] Cannot update field: activeYear is not set"
        );
        const { showNotification } = await import("@mantine/notifications");
        showNotification({
          title: "Error",
          message: "Tax year is not set. Please select a tax year.",
          color: "red",
        });
        throw new Error("Tax year is required for compliance");
      }

      try {
        const { updateProfileField: updateAPI } = await import("../api");
        const { showNotification } = await import("@mantine/notifications");

        await updateAPI(userId, { key, value }, activeYear);

        // Don't update the store here - just save to backend
        // Store will be refreshed when user locks (exits edit mode)
        // This keeps the edit experience smooth with no flashing

        showNotification({
          title: "Success",
          message: `The field "${key}" was successfully updated.`,
          color: "green",
        });
      } catch (error) {
        console.error("[ProfileStore] Update failed:", error);
        const { showNotification } = await import("@mantine/notifications");
        showNotification({
          title: "Error",
          message: "Failed to update field. Please try again.",
          color: "red",
        });
        // On error, refetch to ensure consistency
        await get().refetch();
        throw error;
      }
    },

    addDynamicItem: async (userId: string, key: string) => {
      const activeYear = get().activeYear;

      if (!activeYear) {
        console.error(
          "[ProfileStore] Cannot add dynamic item: activeYear is not set"
        );
        const { showNotification } = await import("@mantine/notifications");
        showNotification({
          title: "Error",
          message: "Tax year is not set. Please select a tax year.",
          color: "red",
        });
        throw new Error("Tax year is required for compliance");
      }

      try {
        const { addDynamicItem: addAPI } = await import("../api");
        const response = await addAPI(userId, { key }, activeYear);

        // Backend response is nested: data.content.content contains the actual profile data
        const profileData = response?.content?.content || response?.content;

        if (profileData) {
          const {
            header,
            body,
            documents,
            ocrState,
            oneClickState,
            oneClickJob,
          } = profileData;

          // ocrState comes as a tuple [state, jobData] from backend
          const parsedOcrState = Array.isArray(ocrState)
            ? ocrState[0]
            : ocrState;

          set({
            headerData: header,
            contentData: body,
            documents: documents || [],
            ocrState: parsedOcrState || "none",
            oneClickState: oneClickState || null,
            job: oneClickJob || null,
          });

          // Also update the OCR data cache if we have an active document
          const activeDocument = get().activeDocument;
          if (activeDocument) {
            get().setOCRData(activeDocument, profileData);
          }
        }
      } catch (error) {
        console.error(
          "[ProfileStore] Add dynamic item failed, refetching:",
          error
        );
        await get().refetch();
      }
    },

    deleteDynamicItem: async (userId: string, key: string) => {
      const activeYear = get().activeYear;

      if (!activeYear) {
        console.error(
          "[ProfileStore] Cannot delete dynamic item: activeYear is not set"
        );
        const { showNotification } = await import("@mantine/notifications");
        showNotification({
          title: "Error",
          message: "Tax year is not set. Please select a tax year.",
          color: "red",
        });
        throw new Error("Tax year is required for compliance");
      }

      try {
        const { deleteDynamicItems: deleteAPI } = await import("../api");
        const response = await deleteAPI(userId, key, activeYear);

        // Backend response is nested: data.content.content contains the actual profile data
        const profileData = response?.content?.content || response?.content;

        if (profileData) {
          const {
            header,
            body,
            documents,
            ocrState,
            oneClickState,
            oneClickJob,
          } = profileData;

          // ocrState comes as a tuple [state, jobData] from backend
          const parsedOcrState = Array.isArray(ocrState)
            ? ocrState[0]
            : ocrState;

          set({
            headerData: header,
            contentData: body,
            documents: documents || [],
            ocrState: parsedOcrState || "none",
            oneClickState: oneClickState || null,
            job: oneClickJob || null,
          });

          // Also update the OCR data cache if we have an active document
          const activeDocument = get().activeDocument;
          if (activeDocument) {
            get().setOCRData(activeDocument, profileData);
          }
        } else {
          console.log("[ProfileStore] No data returned, refetching");
          await get().refetch();
        }
      } catch (error) {
        console.error(
          "[ProfileStore] Delete dynamic item failed, refetching:",
          error
        );
        await get().refetch();
      }
    },

    toggleExtractionEnabled: async (extractionId: number, enabled: boolean) => {
      const { documents } = get();

      // Optimistic update
      const updatedDocuments = documents.map((doc) => ({
        ...doc,
        ocrExtractions: doc.ocrExtractions.map((ext) =>
          ext.id === extractionId ? { ...ext, enabled } : ext
        ),
      }));
      set({ documents: updatedDocuments });

      try {
        await toggleExtraction(extractionId, enabled);
        showNotification({
          title: "Success",
          message: `Extraction ${enabled ? "enabled" : "disabled"}.`,
          color: "green",
        });
      } catch (error) {
        console.error("[ProfileStore] Toggle extraction failed:", error);
        // Rollback
        set({ documents });
        showNotification({
          title: "Error",
          message: "Failed to update extraction. Please try again.",
          color: "red",
        });
      }
    },

    deleteDocument: async (documentId: string) => {
      try {
        // Get document info before deletion for event context
        const { documents, userId } = get();
        const documentToDelete = documents.find(
          (doc) => doc.docId === documentId
        );

        // Call API to archive document on backend
        await deleteDoc(documentId);

        // Emit debug event for document deletion
        const { emitDebugEvent } =
          await import("@utils/debug/eventIntegration");
        emitDebugEvent("action", "DOCUMENT_DELETED", "web", {
          documentId: documentId,
          fileName: documentToDelete?.label?.substring(0, 100) || "unknown",
          clientId: userId || "unknown",
          formType: documentToDelete?.key || "unknown",
        });

        // Optimistically remove document from local state
        const updatedDocuments = documents.filter(
          (doc) => doc.docId !== documentId
        );
        set({ documents: updatedDocuments, hasArchivedDocuments: true });

        // Clean up OCR data cache for deleted document
        get().removeOCRData(documentId);

        showNotification({
          title: "Success",
          message: "Document deleted successfully.",
          color: "green",
        });
      } catch (error) {
        console.error("[ProfileStore] Delete document failed:", error);
        showNotification({
          title: "Error",
          message: "Failed to delete document. Please try again.",
          color: "red",
        });
        throw error;
      }
    },
  }))
);
